import { MouseEvent, useEffect, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import { useAppSelector } from "app/hooks";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { useGetAccessiblePracticesQuery } from "services/practices";

import useSwitchPractice from "./use-switch-practice.hook";

const SEARCH_DEBOUNCE_MS = 250;
const PRACTICE_PAGE_SIZE = 25;

/**
 * Practice switcher trigger + dropdown. Lives inside the practice context header
 * (PracticeContextBanner), which owns the multi-practice gate and the persona
 * colour — so the trigger inherits the header's text colour. The whole "name ▾"
 * is one button (one click target).
 */
export default function PracticeSelector() {
  const currentPractice = useAppSelector(selectCurrentPractice);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { switchToPractice, isLoading } = useSwitchPractice();

  const open = Boolean(anchorEl);

  // Debounce the search input so each keystroke doesn't fire a server fetch.
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  // Only fetch while the menu is open. Suno staff see ALL practices, so this is
  // server-searched + paginated.
  const { data: practices = [], isFetching } = useGetAccessiblePracticesQuery(
    { search: debouncedSearch, limit: PRACTICE_PAGE_SIZE },
    { skip: !open }
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  return (
    <>
      <Button
        data-testid="practice-selector"
        aria-label="Switch practice"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleClick}
        disabled={isLoading}
        color="inherit"
        endIcon={
          isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        sx={{
          textTransform: "none",
          fontWeight: 700,
          py: 0,
          minHeight: 28,
          // Show the full practice name (no truncation) — the header is
          // full-width and centred, so long names have room.
          whiteSpace: "nowrap"
        }}
      >
        {currentPractice?.name}
        {currentPractice?.id != null && (
          <Box
            component="span"
            sx={{ ml: 0.75, fontWeight: 400, opacity: 0.8 }}
          >
            #{currentPractice.id}
          </Box>
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // Cap the list height so a long practice list (Suno staff see all)
        // scrolls inside the menu instead of overflowing the viewport.
        MenuListProps={{
          sx: { minWidth: "20rem", pt: 0, maxHeight: "60vh", overflowY: "auto" }
        }}
      >
        <Box
          sx={{ px: 1.5, py: 1 }}
          // Keep typing inside the field instead of triggering the Menu's
          // built-in typeahead; let Escape still close the menu.
          onKeyDown={(event) => {
            if (event.key !== "Escape") event.stopPropagation();
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            // Search is server-side by practice NAME or ID, so hint that
            // explicitly.
            placeholder="Search by name or ID…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ "data-testid": "practice-selector-search" }}
          />
        </Box>

        {isFetching && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isFetching && practices.length === 0 && (
          <MenuItem disabled>No practices found</MenuItem>
        )}

        {!isFetching &&
          practices.map((practice) => (
            <MenuItem
              key={practice.id}
              selected={
                practice.is_current || practice.id === currentPractice?.id
              }
              onClick={() => {
                switchToPractice(practice.id, currentPractice?.id);
                handleClose();
              }}
            >
              <ListItemText
                primary={practice.name}
                secondary={
                  practice.is_home
                    ? `#${practice.id} · Home`
                    : `#${practice.id}`
                }
              />
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
